/**
 * YoYChip — small year-over-year delta badge.
 *
 * Same visuals as HeroKpiProgressBar's internal YoYBadge (marquee-up,
 * ink-flat, rose-down) so the Dashboard + template-analytics pages
 * carry consistent YoY signal without composing the full hero tile.
 *
 * Render contract:
 *   - `pct === null` | `undefined` | `NaN` → render nothing (caller decides
 *     whether to show a "no prior event" empty-state elsewhere)
 *   - |pct| < 0.5 → flat (neutral dash, ink-soft)
 *   - pct > 0 → up (marquee-500 magenta)
 *   - pct < 0 → down (rose-500)
 *
 * V2.5-B Fork (2026-04-20). Additive shared component; does not touch
 * Tier A HeroKpiProgressBar.
 */

import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'

export interface YoYChipProps {
  /**
   * Year-over-year percentage change. `null` / `undefined` / non-finite
   * → component renders null (caller handles absence).
   */
  pct: number | null | undefined
  /** Override the "vs last year" trailing copy. */
  trailing?: string
  /** Override the testid. */
  'data-testid'?: string
}

export function YoYChip({ pct, trailing = 'vs last year', 'data-testid': testId }: YoYChipProps) {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) return null

  const flat = Math.abs(pct) < 0.5
  const up = !flat && pct > 0
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight
  const skin = flat
    ? 'border-hairline bg-surface-stripe text-ink-soft dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300'
    : up
      ? 'border-marquee-200 bg-marquee-50 text-marquee-700 dark:border-marquee-700 dark:bg-marquee-900/30 dark:text-marquee-200'
      : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-900/30 dark:text-rose-200'

  const direction = flat ? 'flat' : up ? 'up' : 'down'
  // Codex 2026-04-20 residual: when |pct| < 0.5 the direction is flat,
  // but naive formatting of pct=-0.2 produced "-0.2%" — inconsistent
  // signal vs the neutral-dash icon. Flat badges now read "~0%" so the
  // copy matches the visual treatment.
  const formatted = flat ? '~0%' : `${up ? '+' : ''}${pct.toFixed(1)}%`
  const resolvedTestId = testId ?? 'yoy-chip'

  return (
    <span
      data-testid={resolvedTestId}
      data-direction={direction}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${skin}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span className="tabular-nums" aria-label={`${pct.toFixed(1)}%`}>{formatted}</span>
      <span className="text-[10px] font-normal opacity-80">{trailing}</span>
    </span>
  )
}
